// USAGE:
// 1. import a collection of effects into the animated scene: src/assets/styles/animations.scss

import type { ShallowRef } from "vue";

// 2. compose the animateScene method using:
// >> animateElement(element, 'animation-name', 'animation-duration', 'animation-delay')

// 3. statically assign appropriate init animation state to each animated element in the form of CSS class; e.g. animated-element--faded

export const animateElement = (
	element: HTMLElement,
	style: string,
	duration = "1s",
	delay: number,
	timingFunction: string | null = null,
	iterationCount: string | null = null,
): void => {
	element.style.animationName = style;
	element.style.animationDuration = duration;
	element.style.animationFillMode = "forwards";
	element.style.animationDelay = `${delay}ms`;

	if (timingFunction) {
		element.style.animationTimingFunction = timingFunction;
	}

	if (iterationCount) {
		element.style.animationIterationCount = iterationCount;
	}
};

export const resetAnimateElement = (element: HTMLElement): void => {
	element.style.animationName = "";
	element.style.animationDuration = "";
	element.style.animationFillMode = "";
	element.style.animationDelay = "";
};

export const resetAnimation = (
	refs: Record<string, Readonly<ShallowRef<HTMLDivElement | null>>>,
): void => {
	Object.keys(refs).forEach((refElement: string): void => {
		if (refs[refElement].value) {
			refs[refElement].value.classList.remove("animated-element--faded");
			resetAnimateElement(refs[refElement].value);
		}
	});
};

export const skipAnimationOnClick = (
	e: MouseEvent,
	refs: Record<string, Readonly<ShallowRef<HTMLDivElement | null>>>,
	condition: boolean,
	selectors: Array<[selector: string, cssClass: string]>,
): void => {
	if (condition) {
		const targetInRefs = Object.values(refs).some(
			(ref: ShallowRef): boolean => ref.value?.contains(e.target),
		);
		if (!targetInRefs) {
			resetAnimation(refs);

			if (selectors) {
				selectors.forEach(
					([selector, cssClass]: [string, string]): void => {
						if (!refs[selector].value) return;
						handleElements(refs[selector].value, cssClass);
					},
				);
			}
		}
	}
};

const handleElements = (element: HTMLElement, cssClass: string): void => {
	if (element) {
		const selectedElements = element.querySelectorAll(
			cssClass,
		) as NodeListOf<HTMLElement>;
		selectedElements.forEach((el: HTMLElement): void => {
			el.classList.remove("animated-element--faded");
			resetAnimateElement(el);
		});
	}
};

// NOTE: Step-scrolls through elements one-by-one, moving forward (top → bottom) and aligning the target to a TOP offset.
export const scrollStepwiseDown = (
	container: HTMLElement, // IMPORTANT: Only when using ScrollFade: ensure scrolling happens inside `.scroll-content`.
	el: HTMLElement,
	offset = 0,
): void => {
	const cRect = container.getBoundingClientRect();
	const eRect = el.getBoundingClientRect();

	const y = eRect.top - cRect.top + container.scrollTop - offset;

	container.scrollTo({
		top: Math.max(0, y),
		behavior: "smooth",
	});
};

// NOTE: Step-scrolls downward only when the next item falls outside the visible viewport;
// if the target is already fully visible, no scrolling is performed.
export const scrollStepwiseDownIfNeeded = (
	container: HTMLElement, // IMPORTANT: Only when using ScrollFade: ensure scrolling happens inside `.scroll-content`.
	el: HTMLElement,
	offsetTop = 0,
	offsetBottom = 0,
): void => {
	const cRect = container.getBoundingClientRect();
	const eRect = el.getBoundingClientRect();

	// visible zone in the container (in px within the viewport)
	const viewTop = cRect.top + offsetTop;
	const viewBottom = cRect.bottom - offsetBottom;

	const isAbove = eRect.top < viewTop;
	const isBelow = eRect.bottom > viewBottom;

	if (!isAbove && !isBelow) return; // already visible -> do nothing

	// calculate the scrollTop (within the scroll container)
	const currentTop = container.scrollTop;

	let targetTop = currentTop;

	if (isAbove) {
		// position the element so that it is at the top of the visible zone
		const delta = viewTop - eRect.top;
		targetTop = currentTop - delta;
	} else if (isBelow) {
		// position the element so that it is at the bottom of the visible zone
		const delta = eRect.bottom - viewBottom;
		targetTop = currentTop + delta;
	}

	container.scrollTo({
		top: Math.max(0, targetTop),
		behavior: "smooth",
	});
};
